// The calendar in the person's time zone (`lib/time.js`). Days are `YYYY-MM-DD` strings of that
// zone's calendar; the API sends timed values as ISO with an offset, all-day ones as dates (end
// exclusive) and floating ones as wall times, and this file turns them into wall Dates (their UTC
// fields are the zone's clock), formatted with `timeZone: 'UTC'`. Weeks start on Monday. See
// doc/feature/calendar.md and doc/feature/time-zone.md.

import { fromWall, isoOffset, offsetMinutes, today as zoneToday, userZone, wallDate, wallText } from './time.js'

export const VIEWS = ['day', 'week', 'month', 'year', 'agenda']
const DAY_MS = 86400000
/** Days the agenda shows from its date. */
export const AGENDA_DAYS = 30

const pad = (n) => String(n).padStart(2, '0')

/** `YYYY-MM-DD` of a wall Date. */
export function isoDate(date) {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

/** The wall Date of a `YYYY-MM-DD` day's midnight. */
export function parseDay(day) {
  const [y, m, d] = String(day).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

export function addDays(day, n) {
  const date = parseDay(day)
  date.setUTCDate(date.getUTCDate() + n)
  return isoDate(date)
}

export function today() {
  return zoneToday()
}

/** A valid day, else today. */
export function validDay(day) {
  return /^\d{4}-\d{2}-\d{2}$/.test(day || '') && !Number.isNaN(parseDay(day).getTime()) ? day : today()
}

/** The Monday of the week a day is in. */
export function weekStart(day) {
  const date = parseDay(day)
  return addDays(day, -((date.getUTCDay() + 6) % 7))
}

/** The days a view shows around `day`: a week, one day, the whole weeks of a month, its year, or the agenda. */
export function viewDays(view, day) {
  let first = day
  let count = 1
  if (view === 'week') {
    first = weekStart(day)
    count = 7
  } else if (view === 'month') {
    const date = parseDay(day)
    const monthFirst = isoDate(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)))
    const monthLast = isoDate(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)))
    first = weekStart(monthFirst)
    count = Math.round((parseDay(addDays(weekStart(monthLast), 7)) - parseDay(first)) / DAY_MS)
  } else if (view === 'year') {
    const year = parseDay(day).getUTCFullYear()
    first = `${year}-01-01`
    count = Math.round((Date.UTC(year + 1, 0, 1) - Date.UTC(year, 0, 1)) / DAY_MS)
  } else if (view === 'agenda') {
    count = AGENDA_DAYS
  }
  return Array.from({ length: count }, (_, i) => addDays(first, i))
}

/** ISO with the zone's offset at midnight of a day: the window the API takes. */
export function windowEdge(day) {
  return `${day}T00:00:00${isoOffset(offsetMinutes(userZone(), fromWall(day)))}`
}

/** The window the API takes for a run of days: midnight of the first to midnight after the last. */
export function viewWindow(days) {
  return { from: windowEdge(days[0]), to: windowEdge(addDays(days[days.length - 1], 1)) }
}

/**
 * The calendar call for a window, over every calendar I read (a mailbox session's own). `only` is
 * `all_day` (the all-day row's instances, for the year) or `countdown` (events with a countdown).
 */
export function eventsRequest(window_, only) {
  return { path: 'calendar/events', params: only ? { ...window_, only } : window_ }
}

/** The call for the calendars my mailboxes subscribe to, or a mailbox session's own. */
export function subscriptionsRequest(session) {
  return session ? { path: `container/${session.container}/calendar_subscriptions`, params: { mailbox: session.ref } } : { path: 'calendar/subscriptions', params: {} }
}

const PREFS = 'calendar'

/** What this browser keeps for the calendar: `view`, `hide` (hidden calendar refs, comma separated), `night`, `todos` (false: the to-do row folded). */
export function calendarPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREFS)) || {}
  } catch {
    return {}
  }
}

/** Keeps a change to the calendar's prefs in this browser. */
export function rememberCalendar(change) {
  try {
    localStorage.setItem(PREFS, JSON.stringify({ ...calendarPrefs(), ...change }))
  } catch {}
}

/**
 * My calendars: My calendar first for a person (`user`, my `usr_` ref, in the accent color 0),
 * then one per mailbox I read (`globalState.mailboxes`), with their color and whether `hidden`
 * leaves them out, each mailbox with the calendars it subscribes to (hidden by their own ref).
 */
export function calendarList(mailboxes, hidden = [], subscriptions = [], user = '') {
  const mine = user ? [{ ref: user, kind: 'personal', container: null, name: 'My calendar', address: '', prefix: '', domain: '', color: 0, shown: !hidden.includes(user), subscriptions: [] }] : []
  return mine.concat((mailboxes || []).map((mailbox, index) => {
    const prefix = mailbox.aliases?.[0] || ''
    return {
      ref: mailbox.ref,
      kind: 'mailbox',
      container: mailbox.container,
      name: mailbox.display_name || prefix,
      address: mailbox.domains?.length ? `${prefix}@${mailbox.domains[0]}` : prefix,
      prefix,
      domain: mailbox.domains?.[0] || '',
      color: (index % 8) + 1,
      shown: !hidden.includes(mailbox.ref),
      subscriptions: subscriptions
        .filter((subscription) => subscription.mailbox === mailbox.ref)
        .map((subscription) => ({ ...subscription, shown: !hidden.includes(subscription.ref) })),
    }
  }))
}

/**
 * The API's occurrences as the calendar shows them: placed, in the color of the first shown
 * calendar holding them (`calendar`, the one it opens for; a subscribed calendar's own color),
 * the title and the states drawn. An event shows when one of its calendars does (a mailbox
 * session always shows its own) and its subscription does.
 */
export function calendarItems(raw, calendars, session) {
  const colors = Object.fromEntries(calendars.flatMap((calendar) => [[calendar.ref, calendar.color], ...(calendar.subscriptions || []).map((subscription) => [subscription.ref, subscription.color])]))
  const shown = new Set(calendars.filter((calendar) => calendar.shown).map((calendar) => calendar.ref))
  const feeds = new Set(calendars.flatMap((calendar) => (calendar.subscriptions || []).filter((subscription) => subscription.shown).map((subscription) => subscription.ref)))
  const colorOf = (ref) => colors[ref] ?? 1
  return raw.flatMap((item) => {
    const holders = (item.calendars || []).filter((holder) => session || shown.has(holder.calendar))
    if (!holders.length || (item.subscription && !feeds.has(item.subscription))) return []
    const colorsOf = holders.map((holder) => colorOf(holder.calendar))
    return [{
      ...place(item),
      key: `${item.event}:${item.instance}`,
      calendar: holders[0].calendar,
      title: item.summary || '(no title)',
      color: item.subscription ? colorOf(item.subscription) : colorsOf[0],
      colors: item.subscription ? [colorOf(item.subscription)] : colorsOf,
      cancelled: item.status === 'cancelled',
      tentative: item.status === 'tentative',
      needsReply: item.my_partstat === 'needs-action',
      declined: item.my_partstat === 'declined',
    }]
  })
}

/** The day a view moves to from `day` (`dir` is 1 or -1). */
export function stepDay(view, day, dir) {
  const date = parseDay(day)
  if (view === 'month') {
    return isoDate(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + dir, 1)))
  }
  if (view === 'year') {
    const year = date.getUTCFullYear() + dir
    const last = new Date(Date.UTC(year, date.getUTCMonth() + 1, 0)).getUTCDate()
    return isoDate(new Date(Date.UTC(year, date.getUTCMonth(), Math.min(date.getUTCDate(), last))))
  }
  return addDays(day, dir * (view === 'week' ? 7 : view === 'agenda' ? 7 : 1))
}

/** "October 2026", "2026", "Oct 19 - 25, 2026" (in the viewer's locale), "Tuesday, October 20, 2026". */
export function viewTitle(view, day) {
  const date = parseDay(day)
  if (view === 'month') return wallText(date, { month: 'long', year: 'numeric' })
  if (view === 'year') return wallText(date, { year: 'numeric' })
  if (view === 'day') return wallText(date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  const days = viewDays(view, day)
  const format = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  return format.formatRange(parseDay(days[0]), parseDay(days[days.length - 1]))
}

/** A day's label in the viewer's locale, e.g. `{ weekday: 'short' }`. */
export function dayLabel(day, options) {
  return wallText(parseDay(day), options)
}

/**
 * The all-day row of a run of days: each item that covers one of them as a bar from its first to
 * its last day there (`from`, `to` as column indexes), packed into `lane`s that do not overlap.
 */
export function allDayBars(items, days) {
  const first = days[0]
  const last = days[days.length - 1]
  const bars = items
    .filter((item) => inAllDayRow(item) && item.firstDay <= last && item.lastDay >= first)
    .map((item) => ({
      item,
      from: days.indexOf(item.firstDay < first ? first : item.firstDay),
      to: days.indexOf(item.lastDay > last ? last : item.lastDay),
    }))
    .sort((a, b) => a.from - b.from || b.to - b.from - (a.to - a.from))
  const lanes = []
  for (const bar of bars) {
    let lane = lanes.findIndex((end) => end < bar.from)
    if (lane < 0) lane = lanes.length
    lanes[lane] = bar.to
    bar.lane = lane
  }
  return { bars, lanes: lanes.length }
}

/** A value the API sends, as a wall Date: an instant, a date's midnight, or a floating wall time. */
export function toDate(value) {
  const text = String(value || '')
  const date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (date) return new Date(Date.UTC(+date[1], +date[2] - 1, +date[3]))
  const wall = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text)
  if (wall) return new Date(Date.UTC(+wall[1], +wall[2] - 1, +wall[3], +wall[4], +wall[5], +(wall[6] || 0)))
  return wallDate(new Date(text))
}

/** The wall Date of now. */
export function nowWall() {
  return wallDate(new Date())
}

/** Now as ISO with the viewer's offset, on their clock: the `now` calendar search takes. */
export function nowIso(now = new Date()) {
  const wall = wallDate(now)
  return `${isoDate(wall)}T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}:${pad(wall.getUTCSeconds())}${isoOffset(offsetMinutes(userZone(), now))}`
}

/**
 * An occurrence placed on the viewer's calendar: `startAt`/`endAt` Dates, the first and last day
 * it covers (`firstDay`, `lastDay`, inclusive) and its `minutes` length.
 */
export function place(item) {
  const startAt = toDate(item.start)
  let endAt = toDate(item.end)
  if (!(endAt > startAt)) endAt = new Date(startAt.getTime() + (item.all_day ? DAY_MS : 0))
  const firstDay = isoDate(startAt)
  // An end at midnight (and an all-day end, which is exclusive) belongs to the day before.
  const lastDay = isoDate(new Date(endAt.getTime() - (endAt > startAt ? 1 : 0)))
  return { ...item, startAt, endAt, firstDay, lastDay, minutes: Math.round((endAt - startAt) / 60000) }
}

/** Whether a placed item shows in the all-day row: dates, and timed items that span days. */
export function inAllDayRow(item) {
  return item.all_day || item.firstDay !== item.lastDay
}

/** The placed items on one day, all-day row first. */
export function onDay(items, day) {
  return items.filter((item) => item.firstDay <= day && item.lastDay >= day)
}

/**
 * Timed items of one day laid out in overlap columns: `top` and `height` in minutes from local
 * midnight, `col` and `cols` for the width. A run of overlapping items shares the columns.
 */
export function layoutDay(items, day) {
  const dayStart = parseDay(day).getTime()
  const boxes = items
    .map((item) => {
      const top = Math.max(0, (item.startAt - dayStart) / 60000)
      const bottom = Math.min(24 * 60, (item.endAt - dayStart) / 60000)
      return { item, top, height: Math.max(bottom - top, 20) }
    })
    .sort((a, b) => a.top - b.top || b.height - a.height)
  const placed = []
  let group = []
  let groupEnd = -1
  const close = () => {
    const cols = group.reduce((max, box) => Math.max(max, box.col + 1), 0)
    for (const box of group) placed.push({ ...box, cols })
    group = []
  }
  for (const box of boxes) {
    if (box.top >= groupEnd && group.length) close()
    const taken = new Set(group.filter((other) => other.top + other.height > box.top).map((other) => other.col))
    let col = 0
    while (taken.has(col)) col++
    group.push({ ...box, col })
    groupEnd = Math.max(groupEnd, box.top + box.height)
  }
  if (group.length) close()
  return placed
}

// The year, the night and free time, countdowns (see doc/feature/calendar-views.md).

/**
 * The twelve months of `day`'s year for the year view: each month as six weeks from Monday (days
 * of other months marked `inside: false`), each week's bars of all-day and multi-day items clipped
 * to the month (`from`, `to` as weekday columns, packed into `lane`s), and the month's such items
 * in time order.
 */
export function yearMonths(day, items) {
  const year = parseDay(day).getUTCFullYear()
  const long = items.filter(inAllDayRow)
  return Array.from({ length: 12 }, (_, month) => {
    const first = isoDate(new Date(Date.UTC(year, month, 1)))
    const last = isoDate(new Date(Date.UTC(year, month + 1, 0)))
    const start = weekStart(first)
    const weeks = Array.from({ length: 6 }, (_, week) => {
      const days = Array.from({ length: 7 }, (_, index) => addDays(start, week * 7 + index))
      const inside = days.filter((entry) => entry >= first && entry <= last)
      const offset = days.indexOf(inside[0])
      const { bars, lanes } = inside.length ? allDayBars(long, inside) : { bars: [], lanes: 0 }
      return {
        key: days[0],
        days: days.map((entry) => ({ day: entry, num: parseDay(entry).getUTCDate(), inside: entry >= first && entry <= last })),
        bars: bars.map((bar) => ({ ...bar, from: bar.from + offset, to: bar.to + offset })),
        lanes,
      }
    })
    const shown = long.filter((item) => item.firstDay <= last && item.lastDay >= first).sort((a, b) => a.startAt - b.startAt)
    return { month: first, weeks, items: shown }
  })
}

/** The night the week and day grids fold, minutes from midnight: 22:00 to 07:00. */
export const NIGHT = { start: 22 * 60, end: 7 * 60 }
/** An hour of the week and day grids, and a folded night, in pixels. */
export const HOUR_PX = 48
export const BAND_PX = 28

/**
 * The stretches a day grid folds, minutes from midnight: midnight to `early` and `late` to
 * midnight, shrunk to whole hours around `boxes` (`{ top, height }` in minutes) so no event folds.
 */
export function nightBands(boxes, night = NIGHT) {
  let early = night.end
  let late = night.start
  for (const box of boxes) {
    early = Math.min(early, Math.floor(box.top / 60) * 60)
    late = Math.max(late, Math.ceil((box.top + box.height) / 60) * 60)
  }
  return { early: Math.max(early, 0), late: Math.min(late, 24 * 60) }
}

/**
 * The vertical axis of a day grid with its night folded (or `open`): `y(minutes)` and
 * `minutes(y)` in pixels, the `height`, the `hours` to label, the folded `bands`, the `nights` to
 * tint while open, and `lines`, where the hour lines of the day start.
 */
export function timeAxis({ early, late, open = false, hour = HOUR_PX, band = BAND_PX }) {
  const parts = [
    { key: 'early', from: 0, to: early, night: true },
    { key: 'day', from: early, to: late, night: false },
    { key: 'late', from: late, to: 24 * 60, night: true },
  ].filter((part) => part.to > part.from)
  let height = 0
  for (const part of parts) {
    part.folded = part.night && !open
    part.top = height
    part.height = part.folded ? band : ((part.to - part.from) * hour) / 60
    height += part.height
  }
  const last = parts[parts.length - 1]
  const y = (minutes) => {
    const part = parts.find((entry) => minutes < entry.to) || last
    return part.top + ((Math.min(minutes, part.to) - part.from) / (part.to - part.from)) * part.height
  }
  const minutes = (px) => {
    const part = parts.find((entry) => px < entry.top + entry.height) || last
    return part.from + ((Math.max(px, part.top) - part.top) / part.height) * (part.to - part.from)
  }
  const folded = parts.filter((part) => part.folded)
  const hours = []
  for (let at = 60; at < 24 * 60; at += 60) {
    // A band names its own hours, so its edges carry no label of their own.
    if (!folded.some((part) => at >= part.from && at <= part.to)) hours.push({ hour: at / 60, top: y(at) })
  }
  const pick = (part) => ({ key: part.key, from: part.from, to: part.to, top: part.top, height: part.height })
  return {
    height,
    y,
    minutes,
    hours,
    bands: folded.map(pick),
    nights: open ? parts.filter((part) => part.night).map(pick) : [],
    lines: parts.find((part) => !part.folded)?.top % hour || 0,
  }
}

/** Free time: between 08:00 and 20:00, stretches of at least two hours. */
export const FREE = { start: 8 * 60, end: 20 * 60, min: 120 }

/**
 * The free stretches of a day, minutes from midnight (`{ from, to, minutes }`): gaps of at least
 * `free.min` between `max(free.start, from)` and `free.end` that no timed item covers. All-day,
 * cancelled and declined items leave the time free; an item takes at least 20 minutes, as its box.
 */
export function freeBlocks(items, day, from = 0, free = FREE) {
  const dayStart = parseDay(day).getTime()
  const busy = items
    .filter((item) => !item.all_day && !item.cancelled && !item.declined && item.firstDay <= day && item.lastDay >= day)
    .map((item) => {
      const start = (item.startAt - dayStart) / 60000
      return [start, Math.max((item.endAt - dayStart) / 60000, start + 20)]
    })
    .sort((a, b) => a[0] - b[0])
  const blocks = []
  let at = Math.max(free.start, from)
  const gap = (to) => {
    const end = Math.min(to, free.end)
    if (end - at >= free.min) blocks.push({ from: at, to: end, minutes: end - at })
  }
  for (const [start, end] of busy) {
    if (start > at) gap(start)
    at = Math.max(at, end)
  }
  gap(free.end)
  return blocks
}

/**
 * Whether the loaded window (`{ from, to, timed }`, `timed` false when only all-day events were
 * asked for) holds every event of `days`: until it does, the days show no free time.
 */
export function loadedFor(loaded, days) {
  return Boolean(loaded?.timed) && loaded.from <= days[0] && loaded.to >= days[days.length - 1]
}

/** A length of free time, "2 h", "3 h 30 min". */
export function hoursText(minutes) {
  const hours = Math.floor(minutes / 60)
  const rest = Math.round(minutes % 60)
  return rest ? `${hours} h ${rest} min` : `${hours} h`
}

/** Countdowns show on this many days before an event. */
export const COUNTDOWN_DAYS = 30

/** Whole days from one `YYYY-MM-DD` to another. */
export function daysBetween(from, to) {
  return Math.round((parseDay(to) - parseDay(from)) / DAY_MS)
}

/** "12 days to Launch", "1 day to Launch". */
export function countdownText(days, title) {
  return `${days} day${days === 1 ? '' : 's'} to ${title}`
}

/**
 * The countdown lines of each of `days` (`{ [day]: [{ key, title, days, color, text }] }`): every
 * placed item with a countdown, not cancelled, that starts 1 to `max` days later; the nearest
 * instance of each event, the nearest event first.
 */
export function countdowns(items, days, max = COUNTDOWN_DAYS) {
  const counting = items.filter((item) => item.countdown && !item.cancelled)
  const lines = {}
  for (const day of days) {
    const nearest = new Map()
    for (const item of counting) {
      const ahead = daysBetween(day, item.firstDay)
      const id = item.uid || item.event
      if (ahead < 1 || ahead > max || nearest.get(id)?.days <= ahead) continue
      nearest.set(id, { key: item.key, title: item.title, days: ahead, color: item.color, text: countdownText(ahead, item.title) })
    }
    if (nearest.size) lines[day] = [...nearest.values()].sort((a, b) => a.days - b.days)
  }
  return lines
}

/** How far off an instance starting on `firstDay` is: "12 days to go", "Tomorrow", "Today", or '' once over. */
export function countdownAhead(firstDay, day = today()) {
  const ahead = daysBetween(day, firstDay)
  if (ahead > 1) return `${ahead} days to go`
  return ahead === 1 ? 'Tomorrow' : ahead === 0 ? 'Today' : ''
}

// Next up in the mail app (see doc/feature/next-up.md).

/** Next up shows today and the next two days. */
export const NEXT_UP_DAYS = 3
/** Rows a day of Next up lists before "n more". */
export const NEXT_UP_ROWS = 4

/** The days Next up shows from `day`: it and the next two. */
export function nextUpDays(day = today()) {
  return Array.from({ length: NEXT_UP_DAYS }, (_, index) => addDays(day, index))
}

/** Next up's calendars: My calendar (`user`), every mailbox I read, and the subscriptions the calendar shows (`hide` is its hidden refs). */
export function nextUpCalendars(mailboxes, subscriptions = [], hide = [], user = '') {
  const feeds = new Set(subscriptions.map((subscription) => subscription.ref))
  return calendarList(mailboxes, hide.filter((ref) => feeds.has(ref)), subscriptions, user)
}

/** Whether a placed item counts for Next up: not cancelled, not declined. */
const planned = (item) => !item.cancelled && !item.declined

/** "in 25 min", "in 2 h 10 min" for a timed item later today, "Now" while it runs, else ''. */
export function startsIn(item, now = nowWall()) {
  if (item.all_day) return ''
  if (item.startAt <= now) return item.endAt > now ? 'Now' : ''
  if (item.firstDay !== isoDate(now)) return ''
  const minutes = Math.ceil((item.startAt - now) / 60000)
  return minutes < 60 ? `in ${minutes} min` : `in ${hoursText(minutes)}`
}

/**
 * Next up from placed items (`calendarItems`): each of `days` with its planned items not over at
 * `now` (a wall Date), all-day first, then by start, at most `rows` with how many `more`; `next`,
 * the first timed item not over; `join`, the first not over with a link to join.
 */
export function nextUp(items, days, now = nowWall(), rows = NEXT_UP_ROWS) {
  const live = items.filter((item) => planned(item) && (item.all_day ? item.lastDay >= isoDate(now) : item.endAt > now))
  const timed = live.filter((item) => !item.all_day).sort((a, b) => a.startAt - b.startAt)
  const groups = days.map((day, index) => {
    const here = onDay(live, day).sort((a, b) => Number(b.all_day) - Number(a.all_day) || a.startAt - b.startAt)
    return {
      day,
      label: index === 0 ? 'Today' : index === 1 ? 'Tomorrow' : dayLabel(day, { weekday: 'long' }),
      date: dayLabel(day, { month: 'short', day: 'numeric' }),
      rows: here.slice(0, rows).map((item) => ({ ...item, time: item.all_day ? 'All day' : item.firstDay === day ? timeLabel(item.startAt) : `until ${timeLabel(item.endAt)}` })),
      more: Math.max(here.length - rows, 0),
    }
  })
  return { groups, next: timed[0] || null, join: timed.find((item) => item.url) || null }
}

/** Today's countdown lines for events after Next up's days; the ones within show in their day. */
export function nextUpCountdowns(items, days) {
  return (countdowns(items, [days[0]])[days[0]] || []).filter((line) => line.days >= days.length)
}

const timeFormat = { hour: 'numeric', minute: '2-digit' }

/** The time of a wall Date, "10:00 AM". */
export function timeLabel(date) {
  return wallText(date, timeFormat)
}

/** "Tue, Oct 20, 2026, 10:00 - 11:30", or the days of an all-day item. */
export function whenText(item) {
  const placed = item.startAt ? item : place(item)
  const day = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
  if (placed.all_day) {
    const first = wallText(placed.startAt, day)
    return placed.firstDay === placed.lastDay ? first : `${first} - ${dayLabel(placed.lastDay, day)}`
  }
  const from = `${wallText(placed.startAt, day)}, ${timeLabel(placed.startAt)}`
  if (placed.endAt.getTime() === placed.startAt.getTime()) return from
  const to = placed.firstDay === placed.lastDay ? timeLabel(placed.endAt) : `${wallText(placed.endAt, day)}, ${timeLabel(placed.endAt)}`
  return `${from} - ${to}`
}

const WEEKDAYS = { MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday', TH: 'Thursday', FR: 'Friday', SA: 'Saturday', SU: 'Sunday' }
const UNITS = { DAILY: 'day', WEEKLY: 'week', MONTHLY: 'month', YEARLY: 'year' }

/** "Every week on Tuesday, 6 times" from a RRULE; '' for rules it cannot say. */
export function repeatText(rrule) {
  const parts = Object.fromEntries(String(rrule || '').replace(/^RRULE:/i, '').split(';').filter(Boolean).map((part) => part.split('=')))
  const unit = UNITS[(parts.FREQ || '').toUpperCase()]
  if (!unit) return ''
  const every = +parts.INTERVAL > 1 ? `Every ${parts.INTERVAL} ${unit}s` : `Every ${unit}`
  const days = (parts.BYDAY || '').split(',').map((day) => WEEKDAYS[day.replace(/^[-+\d]+/, '').toUpperCase()]).filter(Boolean)
  const on = unit === 'week' && days.length ? ` on ${days.join(', ')}` : ''
  let end = ''
  if (parts.COUNT) end = `, ${parts.COUNT} times`
  else if (parts.UNTIL) {
    const m = /^(\d{4})(\d{2})(\d{2})/.exec(parts.UNTIL)
    if (m) end = `, until ${wallText(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])), { month: 'short', day: 'numeric', year: 'numeric' })}`
  }
  return `${every}${on}${end}`
}

/** My answer to an invite, as a label: `{ label, tone }`, or null when I am not asked. */
export function answerOf(partstat) {
  return {
    'needs-action': { label: 'Needs reply', tone: 'warning' },
    accepted: { label: 'Accepted', tone: 'success' },
    declined: { label: 'Declined', tone: 'danger' },
    tentative: { label: 'Maybe', tone: 'neutral' },
    delegated: { label: 'Delegated', tone: 'neutral' },
  }[partstat] || null
}

/** Why a change waits for Apply, in words. */
export function reasonText(reason) {
  return {
    organizer: 'It names another organizer than the event has.',
    unverified: 'It could not be verified as coming from the organizer.',
    not_invited: 'It answers for someone who is not invited.',
  }[reason] || ''
}

/** Whether an attachment is a calendar object. */
export function isCalendar(contentType, filename) {
  const type = String(contentType || '').toLowerCase()
  return type.startsWith('text/calendar') || type === 'application/ics' || /\.ics$/i.test(filename || '')
}

// Making and changing events (see doc/feature/own-events.md).

export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
/** Grid clicks and drags land on this many minutes. */
export const SLOT_MINUTES = 30

/** `HH:MM` of minutes from midnight. */
export function clockText(minutes) {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
}

/** Minutes from midnight down to the slot they fall in, within the day. */
export function snapMinutes(minutes, step = SLOT_MINUTES) {
  return Math.min(Math.max(Math.floor(minutes / step) * step, 0), 24 * 60 - step)
}

/** The form's start and end of a slot on a day, minutes from midnight; 24:00 is the next day's 00:00. */
export function slotTimes(day, from, to) {
  const at = (minutes) => (minutes >= 24 * 60 ? { date: addDays(day, 1), time: '00:00' } : { date: day, time: clockText(minutes) })
  return { start: at(from), end: at(to) }
}

/** A new event's default slot: the next full hour today, or 09:00 on another day (`now` a wall Date). */
export function defaultSlot(day, now = nowWall()) {
  if (day !== isoDate(now)) return slotTimes(day, 9 * 60, 10 * 60)
  const hour = Math.min(now.getUTCHours() + 1, 23)
  return slotTimes(day, hour * 60, (hour + 1) * 60)
}

/** `{ date, time }` of an API value on its own wall clock: an ISO with offset, a wall time or a date. */
export function wallParts(value) {
  const m = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/.exec(String(value || ''))
  return m ? { date: m[1], time: m[2] || '' } : { date: '', time: '' }
}

/** `{ date, time }` of an API value on my clock (an instant moves to my zone; a date stays). */
export function clockParts(value) {
  const text = String(value || '')
  if (!text || /^\d{4}-\d{2}-\d{2}$/.test(text)) return wallParts(text)
  const date = toDate(text)
  return { date: isoDate(date), time: clockText(date.getUTCHours() * 60 + date.getUTCMinutes()) }
}

/** Weekday codes moved by `by` days, in week order. */
export function shiftDays(days, by) {
  const moved = new Set(days.map((code) => WEEKDAY_CODES[(((WEEKDAY_CODES.indexOf(code) + by) % 7) + 7) % 7]))
  return WEEKDAY_CODES.filter((code) => moved.has(code))
}

/** `YYYY-MM-DD` of an instant in a zone. */
function dayIn(date, zone) {
  return isoDate(wallDate(date, zone || userZone()))
}

/** The form's repeat fields from a RRULE; a timed UNTIL is read back on the event's clock. */
export function repeatFields(rrule, zone) {
  const parts = Object.fromEntries(String(rrule || '').replace(/^RRULE:/i, '').split(';').filter(Boolean).map((part) => part.split('=')))
  const repeat = { DAILY: 'daily', WEEKLY: 'weekly', MONTHLY: 'monthly', YEARLY: 'yearly' }[(parts.FREQ || '').toUpperCase()] || 'none'
  const days = repeat === 'weekly' ? (parts.BYDAY || '').split(',').map((day) => day.toUpperCase()).filter((day) => WEEKDAY_CODES.includes(day)) : []
  let until = ''
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z)?)?$/.exec(parts.UNTIL || '')
  if (m && m[7]) until = dayIn(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])), zone)
  else if (m) until = `${m[1]}-${m[2]}-${m[3]}`
  return { repeat, days, until, count: parts.COUNT ? String(parts.COUNT) : '', ends: parts.COUNT ? 'count' : until ? 'until' : 'never' }
}

/** The weekday code of a day: `MO` .. `SU`. */
export function weekdayCode(day) {
  return WEEKDAY_CODES[(parseDay(day).getUTCDay() + 6) % 7]
}

// Making an event from an email (see doc/feature/event-from-email.md).

/** Minutes from `day`'s midnight to a form value `{ date, time }`, kept within the day. */
function minutesIn(day, value) {
  const [hours, minutes] = String(value.time || '00:00').split(':').map(Number)
  const at = (parseDay(value.date) - parseDay(day)) / 60000 + hours * 60 + minutes
  return Math.min(Math.max(at, 0), 24 * 60)
}

/**
 * One day as a strip of hours for the event form: the timed items of `day` (from `calendarItems`)
 * as boxes with `left` and `width` in percent of the hours shown and their overlap `row`, the
 * all-day ones apart, and the new event's `slot` (`{ start, end }` form values, or null) the same
 * way. It shows 08:00 to 18:00, widened to the whole hours around everything on it.
 */
export function dayStrip(items, day, slot) {
  const here = onDay(items, day)
  const timed = layoutDay(here.filter((item) => !inAllDayRow(item)), day)
  const range = slot ? { from: minutesIn(day, slot.start), to: minutesIn(day, slot.end) } : null
  const edges = timed.flatMap((box) => [box.top, box.top + box.height]).concat(range ? [range.from, range.to] : [])
  const first = Math.floor(Math.min(8 * 60, ...edges) / 60)
  const last = Math.min(24, Math.ceil(Math.max(18 * 60, ...edges) / 60))
  const span = (last - first) * 60
  const at = (top, length) => ({ left: ((top - first * 60) / span) * 100, width: (Math.min(length, first * 60 + span - top) / span) * 100 })
  return {
    hours: Array.from({ length: last - first + 1 }, (_, i) => ({ hour: first + i, left: ((i * 60) / span) * 100 })),
    allDay: here.filter(inAllDayRow),
    boxes: timed.map((box) => ({ ...box.item, ...at(box.top, box.height), row: box.col })),
    rows: Math.max(1, ...timed.map((box) => box.cols)),
    slot: range ? at(range.from, Math.max(range.to - range.from, 15)) : null,
  }
}

// Reminders: my lead times on an event, minutes before its start (see doc/feature/reminders.md).

/** Longest lead time: four weeks. */
export const LEAD_MAX = 40320
/** Most lead times on one event. */
export const LEADS_MAX = 5
/** The lead times offered to pick from. */
export const LEAD_PRESETS = [0, 5, 10, 15, 30, 60, 120, 1440, 2880, 10080]

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

/** A lead time in words: `At start`, `1 hour 30 minutes before`, `2 weeks before`. */
export function leadLabel(minutes) {
  const total = Math.max(0, Math.round(Number(minutes) || 0))
  if (!total) return 'At start'
  if (total % 10080 === 0) return `${plural(total / 10080, 'week')} before`
  const days = Math.floor(total / 1440)
  const hours = Math.floor((total % 1440) / 60)
  const rest = total % 60
  const parts = [days && plural(days, 'day'), hours && plural(hours, 'hour'), rest && plural(rest, 'minute')].filter(Boolean)
  return `${parts.join(' ')} before`
}

/** Lead times as the API keeps them: whole minutes in 0..LEAD_MAX, unique, ascending, at most LEADS_MAX. */
export function normalizeLeads(list) {
  const valid = (list || []).map(Number).filter((minutes) => Number.isInteger(minutes) && minutes >= 0 && minutes <= LEAD_MAX)
  return [...new Set(valid)].sort((a, b) => a - b).slice(0, LEADS_MAX)
}
